import { LockOutlined } from '@ant-design/icons'
import { Form, Input, Spin, Switch } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { AccountBean } from 'common/api/api'
import { accountApi } from 'common/api/entitiesApi'
import { AnchorsForm } from 'common/components/AnchorsForm'
import { ButtonsPanel } from 'common/components/ButtonsPanel'
import { RoleSelector } from 'common/components/dictionary/RoleSelector'
import { TimeZoneSelector } from 'common/components/dictionary/TimeZoneSelector.tsx'
import { FormCard } from 'common/components/FormCard'
import { AdvertiserSelector } from 'common/components/reloadable/AdvertiserSelector'
import { AgencySelector } from 'common/components/reloadable/AgencySelector.tsx'
import { PublisherSelector } from 'common/components/reloadable/PublisherSelector'
import { useAppContext } from 'common/context/AppContext'
import { useUserContext } from 'common/context/UserContext'
import { queueListRepositionOnModifiedChange } from 'common/util/listNavigation.ts';
import { apiCall, FormMode, showError } from 'common/util/Util'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

export interface AccountFormProps {
  mode: FormMode
  admin?: boolean
}

const AccountForm = ({ mode, admin }: AccountFormProps) => {
  const appContext = useAppContext()
  const { user } = useUserContext()
  const navigateTo = useNavigate()
  const [form] = Form.useForm<AccountBean>()
  const [loading, setLoading] = useState(false)
  const { id } = useParams<{ id?: string }>()
  const idNum = id ? parseInt(id) : 0
  const isSelfEdit = mode === FormMode.edit && user?.id === idNum

  const [entity, setEntity] = useState<AccountBean>({
    active: true,
  } as AccountBean)

  const [showAdvs, setShowAdvs] = useState(false)
  const [showPubs, setShowPubs] = useState(false)

  const handleRoleChange = (role: AccountBean.RoleEnum) => {
    const advRole = role === AccountBean.RoleEnum.Advertiser
    const pubRole = role === AccountBean.RoleEnum.Publisher
    setShowAdvs(advRole)
    setShowPubs(pubRole)

    // Reset dependent fields
    if (!advRole) {
      form.resetFields(['advertiserId'])
    }
    if (!pubRole) {
      form.resetFields(['publisherId'])
    }

    setEntity((prev) => ({ ...prev, role }))
  }

  const handleAgencyChange = (agencyId: number) => {
    if (mode == 'create') {
      form.resetFields(['advertiserId'])
      form.resetFields(['publisherId'])

      setEntity((prev) => ({ ...prev, agencyId }))
    }
  }

  useEffect(() => {
    if (mode === FormMode.edit) {
      apiCall(
        setLoading,
        appContext,
        () => accountApi.account(idNum),
        (data) => {
          if (data && data.data) {
            setEntity(data.data)
            setShowAdvs(!!data.data.advertiserId)
            setShowPubs(!!data.data.publisherId)
            form.setFieldsValue({
              ...data.data,
              currentPassword: undefined,
              password: undefined,
              passwordConfirm: undefined,
            })
          }
        },
      )
    }
  }, [id])

  const deleteAccount = () => {
    apiCall(
      setLoading,
      appContext,
      () => accountApi.deleteAccount(idNum),
      () => navigateTo('/account'),
    )
  }

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const newEntity: AccountBean = {
        ...entity,
        ...values,
        active: entity.active,
      }

      const request = mode === 'edit' ? accountApi.editAccount(newEntity, idNum) : accountApi.createAccount(newEntity)

      setLoading(true)
      request
        .then((response) => {
          queueListRepositionOnModifiedChange({
            appContext,
            routeKey: '/account',
            previousEntity: entity,
            responseEntity: response?.data,
            enabled: mode === FormMode.edit
          })

          navigateTo('/account')
        })
        .catch((e) => {
          setLoading(false)
          showError(appContext, e)
        })
    })
  }

  return (
    <Spin spinning={loading}>
      <AnchorsForm form={form} onFinish={handleSubmit}>
        <FormCard
          title="Account"
          extra={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Active</span>
              <Switch
                checked={entity.active}
                onChange={(checked) => setEntity((prev) => ({ ...prev, active: checked }))}
              />
            </div>
          }
        >
          <FormItem name="name" label="Name" rules={[{ required: true, message: 'Please input Account Name' }]}>
            <Input placeholder="Account name" />
          </FormItem>

          <FormItem
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input Account Email' },
              { type: 'email', message: 'Invalid email' },
            ]}
          >
            <Input placeholder="Email" />
          </FormItem>

          <FormItem
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select Role' }]}
            initialValue={entity.role}
          >
            <RoleSelector
              placeholder="Select role"
              onChange={handleRoleChange}
              allowSuperAdmin={admin}
              disabled={mode === 'edit'}
            />
          </FormItem>

          <FormItem
            name="agencyId"
            label="Agency"
            rules={[
              { required: admin && entity.role !== AccountBean.RoleEnum.SuperAdmin, message: 'Agency is required' },
            ]}
            hidden={!admin || !entity.role || entity.role === AccountBean.RoleEnum.SuperAdmin}
          >
            <AgencySelector placeholder="Select agency" onChange={handleAgencyChange} disabled={mode === 'edit'} />
          </FormItem>

          <FormItem
            name="advertiserId"
            label="Advertiser"
            style={{ display: showAdvs ? 'block' : 'none' }}
            rules={[{ required: showAdvs, message: 'Advertiser is required' }]}
          >
            <AdvertiserSelector
              agencyId={entity.agencyId}
              placeholder="Select advertiser"
              disabled={(admin && !entity.agencyId) || mode === 'edit'}
            />
          </FormItem>

          <FormItem
            name="publisherId"
            label="Publisher"
            style={{ display: showPubs ? 'block' : 'none' }}
            rules={[{ required: showPubs, message: 'Publisher is required' }]}
          >
            <PublisherSelector
              placeholder="Select publisher"
              disabled={(admin && !entity.agencyId) || mode === 'edit'}
            />
          </FormItem>

          <FormItem
            name="timezoneId"
            rules={[{ required: true, message: 'Please select Time Zone' }]}
            label="Time Zone"
          >
            <TimeZoneSelector />
          </FormItem>

          {isSelfEdit && (
            <FormItem
              name="currentPassword"
              label="Current Password"
              dependencies={['password']}
              tooltip="Required to change your password"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (getFieldValue('password') && !value) {
                      return Promise.reject(new Error('Enter your current password'))
                    }
                    return Promise.resolve()
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                autoComplete="current-password"
                placeholder="Current Password"
              />
            </FormItem>
          )}

          <FormItem
            name="password"
            label="Password"
            rules={[{ required: mode === 'create', message: 'Please enter password' }]}
          >
            <Input.Password autoComplete="new-password" placeholder="Password" />
          </FormItem>

          <FormItem
            name="passwordConfirm"
            label="Password Confirmation"
            dependencies={['password']}
            rules={[
              { required: mode === 'create', message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Passwords do not match'))
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" placeholder="Confirm password" />
          </FormItem>
        </FormCard>

        <ButtonsPanel onCancel={() => navigateTo('/account')} showDelete={mode === 'edit'} onDelete={deleteAccount} />
      </AnchorsForm>
    </Spin>
  )
}

export default AccountForm
